import { Card } from "@components/cards/card";
import { InfrastructureListItem } from "@hooks/dtos/IInfrastructureDTO";
import {
  getInfrastructureStatusInfo,
  getInfrastructureTypeIcon,
} from "@hooks/functions/Infrastructure";
import { Masks } from "@hooks/functions/Masks/Masks";
import { IconType } from "react-icons";
import { FaMemory, FaMicrochip, FaNetworkWired, FaServer } from "react-icons/fa";
import { FiCpu, FiHardDrive, FiMapPin, FiMonitor } from "react-icons/fi";

interface IProps {
  infrastructure: InfrastructureListItem;
}

interface IInfoItemProps {
  icon: IconType;
  label: string;
  value: string;
}

const masks = new Masks();
const emptyValue = "Não informado";

const formatGb = (value?: number) =>
  value || value === 0 ? `${masks.formatMilhar(value)} GB` : emptyValue;

const formatCpu = (value?: number) =>
  value || value === 0 ? `${masks.formatMilhar(value)} vCPU` : emptyValue;

const getBadgeTextClass = (variant: string) =>
  variant === "warning" || variant === "info" ? "text-dark" : "text-white";

function InfoItem({ icon: Icon, label, value }: IInfoItemProps) {
  return (
    <div className="d-flex gap-2">
      <Icon className="text-muted flex-shrink-0 mt-1" size={15} />
      <div>
        <small className="text-muted d-block lh-sm">{label}</small>
        <div className="fw-semibold text-break">{value}</div>
      </div>
    </div>
  );
}

export function InfrastructureCard({ infrastructure }: IProps) {
  const status = getInfrastructureStatusInfo(infrastructure.status);
  const TypeIcon = getInfrastructureTypeIcon(infrastructure.typeId);
  const StatusIcon = status.icon;
  const operatingSystem = [
    infrastructure.operatingSystem,
    infrastructure.operatingSystemVersion,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <Card className="h-100 shadow-sm border-0">
      <div className="card-body p-4">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-4">
          <div className="d-flex align-items-start gap-3">
            <div className="bg-primary text-white rounded p-2 flex-shrink-0">
              <TypeIcon size={22} />
            </div>
            <div>
              <h5 className="fw-bold mb-1">{infrastructure.name}</h5>
              <small className="text-muted">
                <FaServer className="me-1" />
                {infrastructure.typeData?.name || emptyValue}
                {infrastructure.region && (
                  <>
                    <span className="mx-2">-</span>
                    <FiMapPin className="me-1" />
                    {infrastructure.region}
                  </>
                )}
              </small>
            </div>
          </div>

          <span
            className={`badge bg-${status.variant} ${getBadgeTextClass(
              status.variant
            )} d-inline-flex align-items-center gap-1 px-3 py-2 flex-shrink-0`}
          >
            <StatusIcon size={13} />
            {status.label}
          </span>
        </div>

        <div className="row g-4">
          <div className="col-6">
            <InfoItem
              icon={FaNetworkWired}
              label="IP principal"
              value={infrastructure.ip || emptyValue}
            />
          </div>
          <div className="col-6">
            <InfoItem
              icon={FiMonitor}
              label="Host"
              value={infrastructure.hostData?.name || emptyValue}
            />
          </div>
          <div className="col-6">
            <InfoItem
              icon={FiCpu}
              label="CPU / vCPU"
              value={`${infrastructure.cpuModel || emptyValue} / ${formatCpu(
                infrastructure.vCpu
              )}`}
            />
          </div>
          <div className="col-6">
            <InfoItem
              icon={FaMemory}
              label="RAM / Swap"
              value={`${formatGb(infrastructure.ramGb)} / ${formatGb(
                infrastructure.ramSwapGb
              )}`}
            />
          </div>
          <div className="col-6">
            <InfoItem
              icon={FiHardDrive}
              label="Disco"
              value={formatGb(infrastructure.diskGb)}
            />
          </div>
          <div className="col-6">
            <InfoItem
              icon={FaMicrochip}
              label="Instância"
              value={infrastructure.instanceType || emptyValue}
            />
          </div>
          <div className="col-6">
            <InfoItem
              icon={FaServer}
              label="Sistema operacional"
              value={operatingSystem || emptyValue}
            />
          </div>
          <div className="col-6">
            <InfoItem
              icon={FiMonitor}
              label="Hostname"
              value={infrastructure.hostname || emptyValue}
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
